import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * LocationsPage - Location Management
 *
 * Configuration page for managing physical locations
 * and kiosk assignments.
 */
import { useState, useCallback } from 'react';
import { ActionIcon, Badge, Box, Button, Checkbox, Group, Menu, Modal, Stack, Table, Text, TextInput, Tooltip, } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useLocations, useCreateLocation, useUpdateLocation, useDeleteLocation } from '@commander/api-client';
import { IconPlus, IconUpload, IconDownload, IconEdit, IconTrash } from '@/components/icons';
import { BulkImportModal } from '@/components/BulkImportModal';
import { PanelHeader } from '@/components/PanelHeader';
/* ====================================
   CSS Keyframe Animations (injected once)
   Premium animations for table rows and page entrance.
   ==================================== */
const locsKeyframes = `
  @keyframes locsRowSlideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes locsPageFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
// Inject keyframes into document head (only once)
if (typeof document !== 'undefined') {
    const styleId = 'locs-keyframes';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = locsKeyframes;
        document.head.appendChild(style);
    }
}
/** Requirement badge - refined corporate style */
function RequirementBadge({ required }) {
    return (_jsx(Badge, { size: "sm", variant: "light", color: required ? 'blue' : 'gray', fw: 400, children: required ? 'Required' : 'Optional' }));
}
const LOCATION_COLUMNS = ['name', 'description', 'requireSameLocationCheckin'];
const LOCATION_SAMPLE_DATA = [
    { name: 'Main Depot', description: 'Primary equipment storage', requiresamelocationcheckin: 'true' },
    { name: 'Satellite Office', description: 'Secondary location', requiresamelocationcheckin: 'false' },
];
export function LocationsPage() {
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newLocation, setNewLocation] = useState({
        name: '',
        description: '',
        requireSameLocationCheckin: false,
    });
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', description: '', requireSameLocationCheckin: false });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const { data, isLoading } = useLocations();
    const createLocation = useCreateLocation();
    const updateLocation = useUpdateLocation();
    const deleteLocation = useDeleteLocation();
    const handleImport = useCallback(async (_file) => {
        // Import endpoint not yet implemented
        return { imported: 0, errors: [{ row: 1, message: 'Import endpoint not yet implemented' }] };
    }, []);
    const handleCreate = useCallback(async () => {
        try {
            await createLocation.mutateAsync(newLocation);
            notifications.show({
                title: 'Location Created',
                message: `Successfully created location ${newLocation.name}`,
                color: 'green',
            });
            setCreateModalOpen(false);
            setNewLocation({ name: '', description: '', requireSameLocationCheckin: false });
        }
        catch {
            notifications.show({
                title: 'Error',
                message: 'Failed to create location',
                color: 'red',
            });
        }
    }, [createLocation, newLocation]);
    const openEdit = useCallback((location) => {
        setEditForm({ name: location.name, description: location.description || '', requireSameLocationCheckin: location.requireSameLocationCheckin });
        setEditTarget(location);
    }, []);
    const handleUpdate = useCallback(async () => {
        if (!editTarget)
            return;
        try {
            await updateLocation.mutateAsync({ id: editTarget.id, ...editForm });
            notifications.show({ title: 'Location Updated', message: `Updated ${editForm.name}`, color: 'green' });
            setEditTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Failed to update location', color: 'red' });
        }
    }, [updateLocation, editTarget, editForm]);
    const handleDelete = useCallback(async () => {
        if (!deleteTarget)
            return;
        try {
            await deleteLocation.mutateAsync(deleteTarget.id);
            notifications.show({ title: 'Location Deleted', message: `Deleted ${deleteTarget.name}`, color: 'green' });
            setDeleteTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Cannot delete location with assigned kiosks', color: 'red' });
        }
    }, [deleteLocation, deleteTarget]);
    const handleExport = useCallback(() => {
        if (!data?.items.length) {
            notifications.show({
                title: 'No Data',
                message: 'No locations to export',
                color: 'yellow',
            });
            return;
        }
        const headers = ['name', 'description', 'requireSameLocationCheckin', 'kioskCount'];
        const csv = [
            headers.join(','),
            ...data.items.map((item) => headers.map((h) => String(item[h] || '')).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `locations-export-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        notifications.show({
            title: 'Export Complete',
            message: `Exported ${data.items.length} location records`,
            color: 'green',
        });
    }, [data]);
    return (_jsxs(Stack, { gap: "lg", style: { animation: 'locsPageFadeIn 400ms ease-out' }, children: [_jsxs(Box, { children: [_jsx(Text, { style: {
                            fontSize: '1.75rem',
                            fontWeight: 500,
                            letterSpacing: '-0.01em',
                            color: 'var(--text-primary)',
                        }, children: "Locations" }), _jsx(Text, { style: {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: 'var(--text-muted)',
                            marginTop: 4,
                        }, children: "Manage physical sites and kiosk deployments" })] }), _jsxs(Box, { style: {
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-colour)',
                    borderRadius: 6,
                    overflow: 'hidden',
                }, children: [_jsx(PanelHeader, { title: "Site Registry", accentColour: "#1e293b", children: _jsxs(Group, { gap: "sm", children: [_jsxs(Menu, { shadow: "md", width: 200, children: [_jsx(Menu.Target, { children: _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconDownload, { size: 14 }), children: "Export" }) }), _jsx(Menu.Dropdown, { children: _jsx(Menu.Item, { onClick: handleExport, children: "Export to CSV" }) })] }), _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconUpload, { size: 14 }), onClick: () => setImportModalOpen(true), children: "Import" }), _jsx(Button, { variant: "light", size: "xs", leftSection: _jsx(IconPlus, { size: 14 }), color: "blue", onClick: () => setCreateModalOpen(true), children: "Add Location" })] }) }), _jsxs(Box, { style: { padding: '1rem' }, children: [_jsxs(Table, { styles: {
                                    table: { borderCollapse: 'separate', borderSpacing: 0 },
                                    th: {
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        color: 'var(--text-muted)',
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        background: 'var(--bg-base)',
                                    },
                                    td: {
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        color: 'var(--text-primary)',
                                        transition: 'background 150ms ease',
                                    },
                                    tr: {
                                        '&:hover td': {
                                            background: 'var(--bg-base)',
                                        },
                                    },
                                }, children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, { children: "Name" }), _jsx(Table.Th, { children: "Description" }), _jsx(Table.Th, { children: "Same Location Checkin" }), _jsx(Table.Th, { children: "Kiosks" }), _jsx(Table.Th, { style: { width: 100 }, children: "Actions" })] }) }), _jsx(Table.Tbody, { children: isLoading ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 5, children: _jsx(Text, { ta: "center", py: "xl", size: "sm", c: "dimmed", children: "Loading..." }) }) })) : data?.items.length === 0 ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 5, children: _jsx(Text, { ta: "center", py: "xl", c: "dimmed", children: "No locations found." }) }) })) : (data?.items.map((location, rowIndex) => (_jsxs(Table.Tr, { style: {
                                                /* Premium: staggered row entrance with fade-in and slide-up */
                                                animation: `locsRowSlideIn 350ms ease-out ${rowIndex * 40}ms backwards`,
                                                /* Premium: left border accent that appears on hover via box-shadow inset */
                                                transition: 'box-shadow 200ms ease, background-color 150ms ease',
                                                boxShadow: 'inset 0 0 0 0 transparent',
                                            }, onMouseEnter: (e) => {
                                                e.currentTarget.style.boxShadow = 'inset 3px 0 0 0 #3B82F6';
                                            }, onMouseLeave: (e) => {
                                                e.currentTarget.style.boxShadow = 'inset 0 0 0 0 transparent';
                                            }, children: [_jsx(Table.Td, { children: _jsx(Text, { fw: 400, children: location.name }) }), _jsx(Table.Td, { children: _jsx(Text, { size: "sm", fw: 400, c: "dimmed", children: location.description || '—' }) }), _jsx(Table.Td, { children: _jsx(RequirementBadge, { required: location.requireSameLocationCheckin }) }), _jsx(Table.Td, { children: _jsxs(Group, { gap: 4, children: [_jsx(Text, { ff: "monospace", fw: 400, size: "sm", c: "dimmed", children: location.kioskCount }), _jsxs(Text, { size: "xs", fw: 400, c: "dimmed", children: ["kiosk", location.kioskCount !== 1 ? 's' : ''] })] }) }), _jsx(Table.Td, { children: _jsxs(Group, { gap: 4, children: [_jsx(Tooltip, { label: "Edit", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "gray", onClick: () => openEdit(location), children: _jsx(IconEdit, { size: 14 }) }) }), _jsx(Tooltip, { label: "Delete", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "red", onClick: () => setDeleteTarget(location), children: _jsx(IconTrash, { size: 14 }) }) })] }) })] }, location.id)))) })] }), data?.items.length ? (_jsx(Box, { style: {
                                    marginTop: '1rem',
                                    paddingTop: '0.75rem',
                                    borderTop: '1px solid var(--border-colour)',
                                }, children: _jsxs(Text, { size: "xs", c: "dimmed", ff: "monospace", children: [data.items.length, " location", data.items.length !== 1 ? 's' : '', " configured"] }) })) : null] })] }), _jsx(BulkImportModal, { opened: importModalOpen, onClose: () => setImportModalOpen(false), title: "Import Locations", entityType: "locations", expectedColumns: LOCATION_COLUMNS, onImport: handleImport, sampleData: LOCATION_SAMPLE_DATA }), _jsx(Modal, { opened: !!editTarget, onClose: () => setEditTarget(null), title: "Edit Location", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Location Name", required: true, value: editForm.name, onChange: (e) => setEditForm({ ...editForm, name: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(TextInput, { label: "Description", value: editForm.description, onChange: (e) => setEditForm({ ...editForm, description: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(Checkbox, { label: "Require same location check-in", description: "Equipment must be returned to this location", checked: editForm.requireSameLocationCheckin, onChange: (e) => setEditForm({ ...editForm, requireSameLocationCheckin: e.currentTarget.checked }), styles: {
                                label: { color: 'var(--text-primary)' },
                                description: { color: 'var(--text-muted)' },
                            } }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setEditTarget(null), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleUpdate, loading: updateLocation.isPending, children: "Save Changes" })] })] }) }), _jsx(Modal, { opened: !!deleteTarget, onClose: () => setDeleteTarget(null), title: "Delete Location", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsxs(Text, { size: "sm", style: { color: 'var(--text-primary)' }, children: ["Are you sure you want to delete ", _jsx("strong", { children: deleteTarget?.name }), "? Locations with assigned kiosks cannot be deleted."] }), _jsxs(Group, { justify: "flex-end", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setDeleteTarget(null), children: "Cancel" }), _jsx(Button, { color: "red", onClick: handleDelete, loading: deleteLocation.isPending, children: "Delete Location" })] })] }) }), _jsx(Modal, { opened: createModalOpen, onClose: () => setCreateModalOpen(false), title: "Add Location", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Location Name", placeholder: "Main Depot", required: true, value: newLocation.name, onChange: (e) => setNewLocation({ ...newLocation, name: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(TextInput, { label: "Description", placeholder: "Primary equipment storage facility", value: newLocation.description, onChange: (e) => setNewLocation({ ...newLocation, description: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(Checkbox, { label: "Require same location check-in", description: "Equipment must be returned to this location", checked: newLocation.requireSameLocationCheckin, onChange: (e) => setNewLocation({ ...newLocation, requireSameLocationCheckin: e.currentTarget.checked }), styles: {
                                label: { color: 'var(--text-primary)' },
                                description: { color: 'var(--text-muted)' },
                            } }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setCreateModalOpen(false), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleCreate, loading: createLocation.isPending, disabled: !newLocation.name, children: "Create Location" })] })] }) })] }));
}
//# sourceMappingURL=LocationsPage.js.map